/**
 * ServiceHistoryPDF — branded "Service History" document for a customer.
 *
 * Mirrors the InvoicePDF header/footer language so a workshop's paperwork reads
 * as one family, but this is a RECORD, not a financial instrument:
 *   - grouped by vehicle (a "full service history" is what a seller hands a buyer);
 *   - no balance due, pay buttons, bank details, VAT breakdown or internal notes;
 *   - prices are optional (showPrices) — some owners want a priced record, others
 *     just "what was done, when, at what mileage".
 * Identity is passed in live (fetched at generation) — there is no point-in-time
 * snapshot because this is not a legal/financial document.
 */
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { PdfPageFooter } from '../../../lib/PdfPageFooter';
import { isWhiteLabelTier } from '../../../lib/tiers';
import { formatServiceMileage, type ServiceMileageSource } from './mileage';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';
import { DOC } from '../../../lib/documentPalette';

export interface ServiceHistoryIdentity {
    name: string;
    logoBase64?: string;
    logo_url?: string;
    address?: string;
    phone?: string;
    email?: string;
    vat_number?: string;
    accent?: string;
    tier?: string | null;
}

export interface ServiceLineItem {
    type?: string; // 'part' | 'labour' | 'service'
    description?: string;
    quantity?: number;
    total?: number;
}

export interface ServiceRecord {
    service_date: string;
    mileage?: number | null;
    /**
     * Where the mileage came from. A 'mot' reading is the odometer at the
     * vehicle's last test, so it is printed with "at last MOT" beside it — this
     * document goes to the customer, and an unqualified figure reads as the
     * mileage at the time of the work.
     */
    mileage_source?: string | null;
    title?: string | null;
    summary?: string | null;
    line_items?: ServiceLineItem[];
    total?: number | null;
}

export interface ServiceHistoryVehicleGroup {
    vehicle: { vrm?: string; make?: string; model?: string; year?: number | null } | null;
    records: ServiceRecord[];
}

export interface ServiceHistoryPDFProps {
    identity: ServiceHistoryIdentity;
    customer: { full_name?: string; email?: string; phone?: string };
    groups: ServiceHistoryVehicleGroup[];
    showPrices: boolean;
    generatedDate?: string;
}

const C = DOC;

const s = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        lineHeight: 1.4,
        color: C.body,
        backgroundColor: C.white,
        paddingBottom: 40,
    },

    header: { padding: '24 35 18 35', borderBottom: `1px solid ${C.line}` },
    logoRow: { marginBottom: 16 },
    logoBox: { width: 48, height: 48 },
    logo: { width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 },

    row: { flexDirection: 'row', alignItems: 'flex-start' },
    colLeft: { flex: 1.4, flexShrink: 0, flexGrow: 1.4, paddingRight: 24 },
    colRight: { flex: 1, flexShrink: 0, flexGrow: 1, alignItems: 'flex-end' },

    label: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 },
    h1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 2 },
    sm: { fontSize: 8, color: C.muted, marginBottom: 1.5 },
    vat: { fontSize: 7, color: C.label, marginTop: 4 },

    docTitle: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: C.black, letterSpacing: 1.2, textTransform: 'uppercase', textAlign: 'right', marginBottom: 2 },
    docMeta: { fontSize: 8, color: C.muted, textAlign: 'right', marginBottom: 1 },

    customerBox: { padding: '12 35 0 35', marginTop: 12 },
    customerName: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 1 },

    /* Vehicle group */
    vehicleHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 16,
        marginHorizontal: 35,
        paddingVertical: 6,
        paddingHorizontal: 10,
        backgroundColor: C.bg,
        borderRadius: 4,
        border: `1px solid ${C.line}`,
    },
    vehicleReg: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black, letterSpacing: 0.5 },
    vehicleName: { fontSize: 8, color: C.muted },

    /* Service record */
    record: { marginHorizontal: 35, paddingVertical: 9, borderBottom: `1px solid ${C.faintLine}` },
    recTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' },
    recDate: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black },
    // Right-aligned so the qualifier tucks under the figure rather than
    // widening the row — this line shares a row with the service date.
    recMileageWrap: { alignItems: 'flex-end' },
    recMileageBig: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.body },
    recMileageMuted: { fontSize: 8, color: C.label, fontFamily: 'Helvetica' },
    recTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.dark, marginTop: 4 },

    workLabel: { fontSize: 6.5, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 5, marginBottom: 2 },
    liRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 2 },
    liDesc: { fontSize: 8, color: C.body, flex: 4 },
    liTotal: { fontSize: 8, color: C.muted, flex: 1, textAlign: 'right' },

    recTotalRow: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 5, gap: 10 },
    recTotalLabel: { fontSize: 8, color: C.muted },
    recTotal: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black },

    /* Summary */
    summaryBox: {
        marginTop: 18,
        marginHorizontal: 35,
        padding: 12,
        backgroundColor: C.bg,
        borderRadius: 5,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    summaryLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black },
    summaryVal: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.black },

    empty: { margin: 35, fontSize: 9, color: C.muted, textAlign: 'center' },
    thankYou: { padding: '18 35 10 35', textAlign: 'center', fontSize: 8, color: C.muted },
});

const fmt = (v: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

export const ServiceHistoryPDF = ({ identity, customer, groups, showPrices, generatedDate }: ServiceHistoryPDFProps) => {
    const accent = identity.accent || DEFAULT_DOCUMENT_ACCENT;
    const whiteLabel = isWhiteLabelTier(identity.tier);
    const hasLogo = !!(identity.logoBase64 || identity.logo_url);

    const allRecords = groups.flatMap((g) => g.records);
    const serviceCount = allRecords.length;
    const grandTotal = allRecords.reduce((sum, r) => sum + Number(r.total || 0), 0);

    return (
        <Document>
            <Page size="A4" style={s.page}>
                {/* Fixed footer on every page */}
                <PdfPageFooter reference={identity.name} branded={!whiteLabel} />

                {/* Accent bar (filled — prints reliably) */}
                <View style={{ height: 6, backgroundColor: accent }} />

                {/* Header */}
                <View style={s.header}>
                    {hasLogo && (
                        <View style={s.logoRow}>
                            <View style={s.logoBox}>
                                <Image src={identity.logoBase64 || identity.logo_url} style={s.logo} />
                            </View>
                        </View>
                    )}

                    <View style={s.row}>
                        <View style={s.colLeft}>
                            <Text style={s.label}>Prepared By:</Text>
                            <Text style={s.h1}>{identity.name}</Text>
                            {identity.address ? <Text style={s.sm}>{identity.address.replace(/\\n/g, '\n')}</Text> : null}
                            {identity.phone ? <Text style={s.sm}>{identity.phone}</Text> : null}
                            {identity.email ? <Text style={s.sm}>{identity.email}</Text> : null}
                            {identity.vat_number ? <Text style={s.vat}>VAT: {identity.vat_number}</Text> : null}
                        </View>

                        <View style={s.colRight}>
                            <Text style={s.docTitle}>Service History</Text>
                            <Text style={s.docMeta}>Generated: {fmtDate(generatedDate || new Date().toISOString())}</Text>
                            <Text style={s.docMeta}>{serviceCount} service{serviceCount === 1 ? '' : 's'} recorded</Text>
                        </View>
                    </View>
                </View>

                {/* Customer */}
                <View style={s.customerBox}>
                    <Text style={s.label}>Customer:</Text>
                    <Text style={s.customerName}>{customer.full_name || 'Customer'}</Text>
                    {customer.email ? <Text style={s.sm}>{customer.email}</Text> : null}
                    {customer.phone ? <Text style={s.sm}>{customer.phone}</Text> : null}
                </View>

                {serviceCount === 0 && (
                    <Text style={s.empty}>No completed services on record yet.</Text>
                )}

                {/* Grouped by vehicle */}
                {groups.map((group, gi) => {
                    if (group.records.length === 0) return null;
                    const v = group.vehicle;
                    const vehicleName = [v?.year, v?.make, v?.model].filter(Boolean).join(' ');
                    return (
                        <View key={gi}>
                            <View style={s.vehicleHeader} wrap={false}>
                                <Text style={s.vehicleReg}>{v?.vrm || 'Vehicle'}</Text>
                                {vehicleName ? <Text style={s.vehicleName}>{vehicleName}</Text> : null}
                            </View>

                            {group.records.map((rec, ri) => {
                                // A service record documents the WORK done — parts fitted and
                                // service items, not the labour charge. Labour lines are excluded.
                                const workItems = (rec.line_items ?? []).filter(
                                    (li) => (li.type ?? '').toLowerCase() !== 'labour'
                                );
                                const heading = (rec.summary && rec.summary.trim()) || rec.title || 'Service';
                                return (
                                    <View key={ri} style={s.record} wrap={false}>
                                        <View style={s.recTop}>
                                            <Text style={s.recDate}>{fmtDate(rec.service_date)}</Text>
                                            {(() => {
                                                const m = formatServiceMileage(
                                                    rec.mileage,
                                                    rec.mileage_source as ServiceMileageSource,
                                                    'miles',
                                                );
                                                if (!m) return <Text style={s.recMileageMuted}>Mileage not recorded</Text>;
                                                return (
                                                    <View style={s.recMileageWrap}>
                                                        <Text style={s.recMileageBig}>{m.value}</Text>
                                                        {m.qualifier && (
                                                            <Text style={s.recMileageMuted}>{m.qualifier}</Text>
                                                        )}
                                                    </View>
                                                );
                                            })()}
                                        </View>

                                        <Text style={s.recTitle}>{heading}</Text>

                                        {workItems.length > 0 && (
                                            <View>
                                                <Text style={s.workLabel}>Work carried out</Text>
                                                {workItems.map((li, li_i) => (
                                                    <View key={li_i} style={s.liRow}>
                                                        <Text style={s.liDesc}>
                                                            {'• '}
                                                            {li.quantity != null && Number(li.quantity) > 1 ? `${Number(li.quantity)}× ` : ''}
                                                            {li.description || 'Item'}
                                                        </Text>
                                                        {showPrices && (
                                                            <Text style={s.liTotal}>{fmt(Number(li.total || 0))}</Text>
                                                        )}
                                                    </View>
                                                ))}
                                            </View>
                                        )}

                                        {showPrices && (
                                            <View style={s.recTotalRow}>
                                                <Text style={s.recTotalLabel}>Invoice total</Text>
                                                <Text style={s.recTotal}>{fmt(Number(rec.total || 0))}</Text>
                                            </View>
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    );
                })}

                {/* Summary */}
                {serviceCount > 0 && (
                    <View style={s.summaryBox} wrap={false}>
                        <Text style={s.summaryLabel}>
                            {serviceCount} service{serviceCount === 1 ? '' : 's'} across {groups.filter((g) => g.records.length > 0).length} vehicle{groups.filter((g) => g.records.length > 0).length === 1 ? '' : 's'}
                        </Text>
                        {showPrices && <Text style={s.summaryVal}>Total: {fmt(grandTotal)}</Text>}
                    </View>
                )}

                <View style={s.thankYou}>
                    <Text>Thank you for trusting {identity.name} with your vehicle{groups.length === 1 ? '' : 's'}.</Text>
                </View>
            </Page>
        </Document>
    );
};
